import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { CSS3DRenderer } from "three/examples/jsm/renderers/CSS3DRenderer.js";
import { setupEventHandlers } from "./EventHandlers";
import { setupZoomHandlers } from "./ZoomHandler";
import ControlBar from "./ControlBar";
import Pano from "./assets/pano.svg";

/** ✅ ONLY 4 VIEWS */
const BASE_PATHS = [
  "/assets/image1", // view 0
  "/assets/image2", // view 1
  "/assets/image3", // view 2
  "/assets/image4", // view 3
];

const VIEW_NAMES = ["Arial View", "Main Road", "Street View", "Street View 2"];

const LOADER_IMAGES = {
  0: "/assets/view0.png",
  1: "/assets/view1.png",
  2: "/assets/view2.png",
  3: "/assets/view3.png",
};

const initialAngles = [
  { yaw: 269.7, pitch: -85 }, // View 0
  { yaw: -57.64, pitch: -30.45 }, // View 1
  { yaw: -278.3, pitch: 1.7 }, // View 2
  { yaw: 229.3, pitch: 1.3 }, // View 3
];

const normalizeAngle = (angle) => ((angle % 360) + 360) % 360;

/** ✅ ONLY 4 yaw offsets now */
const yawOffsets = [260, normalizeAngle(-250), normalizeAngle(-290), normalizeAngle(260)];

const throttle = (func, limit) => {
  let inThrottle;
  return (...args) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

const createHTMLLabel = (
  iconPath,
  name,
  distance,
  position,
  color = "black",
  backgroundColor = "white"
) => {
  const container = document.createElement("div");
  container.style.position = "absolute";
  container.style.display = "flex";
  container.style.alignItems = "center";
  container.style.backgroundColor = backgroundColor;
  container.style.padding = "3px";
  container.style.pointerEvents = "none";
  container.style.whiteSpace = "nowrap";
  container.style.zIndex = "1001";

  const iconDiv = document.createElement("div");
  iconDiv.style.marginRight = "8px";
  const iconImg = document.createElement("img");
  iconImg.src = iconPath;
  iconImg.style.width = "20px";
  iconImg.style.height = "20px";
  iconDiv.appendChild(iconImg);
  container.appendChild(iconDiv);

  const textDiv = document.createElement("div");
  textDiv.style.display = "flex";
  textDiv.style.flexDirection = "column";
  textDiv.style.color = color;
  textDiv.style.fontFamily = "Montserrat, sans-serif";
  textDiv.style.fontSize = "12px";

  const nameSpan = document.createElement("span");
  nameSpan.textContent = name;
  textDiv.appendChild(nameSpan);

  if (distance) {
    const distanceSpan = document.createElement("span");
    distanceSpan.style.fontSize = "12px";
    distanceSpan.textContent = distance;
    textDiv.appendChild(distanceSpan);
  }

  container.appendChild(textDiv);

  const line = document.createElement("div");
  line.style.position = "absolute";
  line.style.backgroundColor = "#ffffff";
  line.style.width = "2px";
  line.style.pointerEvents = "none";
  line.style.zIndex = "1000";

  const point = document.createElement("div");
  point.style.position = "absolute";
  point.style.width = "10px";
  point.style.height = "10px";
  point.style.backgroundColor = "#ffffff";
  point.style.borderRadius = "50%";
  point.style.pointerEvents = "none";
  point.style.zIndex = "1000";

  return {
    element: container,
    line,
    point,
    position: new THREE.Vector3(...position),
  };
};

const createHTMLLoader = (position, onClick, viewIndex) => {
  const container = document.createElement("div");
  container.style.position = "absolute";
  container.style.display = "flex";
  container.style.flexDirection = "column";
  container.style.alignItems = "center";
  container.style.pointerEvents = "auto";
  container.style.cursor = "pointer";
  container.style.zIndex = "1001";
  container.style.transition = "transform 0.3s ease";
  container.style.width = "120px";
  container.style.minWidth = "120px";
  container.style.maxWidth = "150px";

  container.addEventListener("mousedown", (e) => e.stopPropagation());
  container.addEventListener("touchstart", (e) => e.stopPropagation());

  const textDiv = document.createElement("div");
  textDiv.style.color = "black";
  textDiv.style.fontFamily = "Montserrat, sans-serif";
  textDiv.style.background = "white";
  textDiv.style.fontSize = "10px";
  textDiv.style.padding = "4px";
  textDiv.style.fontWeight = "600";
  textDiv.style.marginBottom = "5px";
  textDiv.textContent = VIEW_NAMES[viewIndex] || `View ${viewIndex}`;
  container.appendChild(textDiv);

  const imageDiv = document.createElement("div");
  imageDiv.style.position = "relative";

  const image = document.createElement("img");
  image.src = LOADER_IMAGES[viewIndex] || "/assets/default_loader_icon.png";
  image.alt = `Navigate to view ${viewIndex}`;
  image.style.width = "35px";
  image.style.height = "35px";
  image.style.border = "3px solid white";
  image.style.transition = "width 0.3s ease, height 0.3s ease";

  imageDiv.appendChild(image);
  container.appendChild(imageDiv);

  container.addEventListener("mouseenter", () => {
    image.style.width = "45px";
    image.style.height = "45px";
    container.style.transform = "scale(1.2)";
  });
  container.addEventListener("mouseleave", () => {
    image.style.width = "35px";
    image.style.height = "35px";
    container.style.transform = "scale(1)";
  });

  container.addEventListener("click", (e) => {
    e.stopPropagation();
    onClick();
  });

  return {
    element: container,
    position: new THREE.Vector3(...position),
    onClick,
  };
};

const createHTMLPopupHotspot = (position, imageSrc, popupText, onOpen, href = null) => {
  const hotspot = document.createElement("div");
  hotspot.style.position = "absolute";
  hotspot.style.width = "25px";
  hotspot.style.height = "25px";
  hotspot.style.backgroundColor = "rgba(255, 255, 255, 0.8)";
  hotspot.style.cursor = "pointer";
  hotspot.style.pointerEvents = "auto";
  hotspot.style.borderRadius = "9px";
  hotspot.style.zIndex = "1001";
  hotspot.style.display = "flex";
  hotspot.style.alignItems = "center";
  hotspot.style.justifyContent = "center";
  hotspot.style.padding = "5px";
  hotspot.style.fontFamily = "Montserrat, sans-serif";
  hotspot.style.fontSize = "24px";
  hotspot.style.fontWeight = "bold";
  hotspot.style.color = "#000";
  hotspot.style.minWidth = "25px";
  hotspot.style.maxWidth = "25px";
  hotspot.textContent = "i";

  hotspot.addEventListener("mousedown", (e) => e.stopPropagation());
  hotspot.addEventListener("touchstart", (e) => e.stopPropagation());

  const popup = document.createElement("div");
  popup.style.position = "absolute";
  popup.style.display = "none";
  popup.style.backgroundColor = "rgba(255, 255, 255, 0.9)";
  popup.style.padding = "10px";
  popup.style.borderRadius = "8px";
  popup.style.boxShadow = "0 4px 8px rgba(0,0,0,0.2)";
  popup.style.zIndex = "1003";
  popup.style.pointerEvents = "auto";
  popup.style.width = "160px";
  popup.style.minWidth = "160px";
  popup.style.maxWidth = "160px";

  popup.addEventListener("mousedown", (e) => e.stopPropagation());
  popup.addEventListener("touchstart", (e) => e.stopPropagation());

  const popupImage = document.createElement("img");
  popupImage.src = imageSrc;
  popupImage.style.width = "100%";
  popupImage.style.height = "auto";
  popupImage.style.borderRadius = "4px";
  popup.appendChild(popupImage);

  const popupTextDiv = document.createElement("div");
  popupTextDiv.textContent = popupText;
  popupTextDiv.style.marginTop = "10px";
  popupTextDiv.style.fontFamily = "Montserrat, sans-serif";
  popupTextDiv.style.fontSize = "14px";
  popupTextDiv.style.color = "#333";
  popupTextDiv.style.textAlign = "center";
  popup.appendChild(popupTextDiv);

  if (href) {
    const sourceLink = document.createElement("a");
    sourceLink.href = href;
    sourceLink.textContent = "Know More";
    sourceLink.target = "_blank";
    sourceLink.rel = "noopener noreferrer";
    sourceLink.style.display = "block";
    sourceLink.style.marginTop = "10px";
    sourceLink.style.fontFamily = "Montserrat, sans-serif";
    sourceLink.style.fontSize = "12px";
    sourceLink.style.color = "#0066cc";
    sourceLink.style.textAlign = "center";
    sourceLink.style.textDecoration = "underline";
    sourceLink.style.cursor = "pointer";
    sourceLink.style.pointerEvents = "auto";
    sourceLink.style.position = "relative";
    sourceLink.style.zIndex = "1004";

    sourceLink.addEventListener("click", (e) => {
      e.stopPropagation();
      window.open(href, "_blank", "noopener,noreferrer");
    });

    sourceLink.addEventListener("mousedown", (e) => e.stopPropagation());
    sourceLink.addEventListener("touchstart", (e) => e.stopPropagation());
    popup.appendChild(sourceLink);
  }

  const closeButton = document.createElement("button");
  closeButton.textContent = "Close";
  closeButton.style.marginTop = "10px";
  closeButton.style.padding = "5px 10px";
  closeButton.style.backgroundColor = "black";
  closeButton.style.color = "#fff";
  closeButton.style.border = "none";
  closeButton.style.borderRadius = "4px";
  closeButton.style.cursor = "pointer";
  closeButton.style.display = "block";
  closeButton.style.marginLeft = "auto";
  closeButton.style.marginRight = "auto";
  closeButton.style.fontFamily = "Montserrat, sans-serif";
  closeButton.style.pointerEvents = "auto";
  closeButton.style.zIndex = "1004";
  popup.appendChild(closeButton);

  let isOpen = false;
  hotspot.addEventListener("click", (e) => {
    e.stopPropagation();
    isOpen = !isOpen;
    popup.style.display = isOpen ? "block" : "none";
    if (isOpen && onOpen) onOpen();
  });

  closeButton.addEventListener("click", (e) => {
    e.stopPropagation();
    isOpen = false;
    popup.style.display = "none";
  });

  return {
    hotspotElement: hotspot,
    popupElement: popup,
    position: new THREE.Vector3(...position),
  };
};

const Tour = () => {
  const mountRef = useRef(null);
  const [loadedLevel, setLoadedLevel] = useState(0);
  const [currentView, setCurrentView] = useState(0);
  const [error, setError] = useState(null);
  const [compassAngle, setCompassAngle] = useState(0);
  const opacityRef = useRef({ value: 1, target: 1 });
  const labelContainerRef = useRef(null);

  const switchView = (viewIndex) => setCurrentView(viewIndex);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) {
      setError("Mount reference is not available.");
      return;
    }

    const canvas = document.createElement("canvas");
    const context = canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
    if (!context) {
      setError("WebGL is not supported by this browser or device.");
      return;
    }

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(100, window.innerWidth / window.innerHeight, 0.1, 3000);

    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({
        antialias: true,
        powerPreference: "high-performance",
        alpha: true,
        preserveDrawingBuffer: false,
        canvas,
        context,
      });
    } catch (err) {
      setError(`Failed to initialize WebGL: ${err.message}`);
      return;
    }

    const pixelRatio = Math.min(window.devicePixelRatio, 2);
    renderer.setSize(window.innerWidth, window.innerHeight, false);
    renderer.setPixelRatio(pixelRatio);
    renderer.domElement.style.width = `${window.innerWidth}px`;
    renderer.domElement.style.height = `${window.innerHeight}px`;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.NoToneMapping;
    mount.appendChild(renderer.domElement);

    const cssRenderer = new CSS3DRenderer();
    cssRenderer.setSize(window.innerWidth, window.innerHeight);
    cssRenderer.domElement.style.position = "absolute";
    cssRenderer.domElement.style.top = "0px";
    cssRenderer.domElement.style.pointerEvents = "none";
    mount.appendChild(cssRenderer.domElement);

    const labelContainer = document.createElement("div");
    labelContainer.style.position = "absolute";
    labelContainer.style.top = "0";
    labelContainer.style.left = "0";
    labelContainer.style.width = "100%";
    labelContainer.style.height = "100%";
    labelContainer.style.pointerEvents = "none";
    labelContainer.style.zIndex = "1000";
    labelContainer.style.transform = "none";
    labelContainer.style.perspective = "none";
    mount.appendChild(labelContainer);
    labelContainerRef.current = labelContainer;

    const cubeSize = 1000;
    const geometry = new THREE.BoxGeometry(cubeSize, cubeSize, cubeSize);
    geometry.scale(1, 1, -1);

    const cube = new THREE.Mesh(
      geometry,
      new THREE.MeshBasicMaterial({
        color: 0xcccccc,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 1,
      })
    );
    scene.add(cube);

    /** ✅ Labels only for View 0 / View 1 */
    const labelsFirstImage = [
      createHTMLLabel("/assets/city.svg", "Sakleshpur Town", "20 Min", [320.99, -22.83, -500.0]),
      createHTMLLabel("/assets/city.svg", "Coorg Border", "2Hr", [-133.97, -9.04, 500.0]),
      createHTMLLabel("/assets/plane.svg", "Mangaluru International Airport", "2 Hr", [-292.18, -25.28, -500.0]),
      createHTMLLabel("/assets/plane.svg", "Hasan", "45 Min (Airport Upcoming)", [367.29, -20.58, 500.0]),
      createHTMLLabel("/assets/temple.svg", "Dharmasthala", "1hr 45min", [145.26, -14.54, -500.0]),
      createHTMLLabel("/assets/temple.svg", "Kuke Subrahmanya", "1hr 30Min", [-500.0, -14.2, -92.67]),
    ];

    const labelsEntrance = [
      createHTMLLabel("/assets/road.svg", "Sakleshpur to Mangalore", "Road", [-194.54, -354.44, -500.0]),
    ];

    /** ✅ Popup only for View 0 */
    const popupHotspotsFirstImage = [
      createHTMLPopupHotspot(
        [376.01, -500.0, -294.83],
        "/assets/view0.png",
        "Invest in a fully managed 4-star resort villa with monthly returns at Clarks Exotica Sakleshpur.",
        () => {}
      ),
    ];

    /** ✅ ONLY 4 VIEW NAV LOADERS */
    const loaders = {
      // from view 0
      toSecond: createHTMLLoader([500.0, -112.68, -2.62], () => switchView(1), 1),
      toThird: createHTMLLoader([25.68, -500.0, -112.13], () => switchView(2), 2),
      toFourth: createHTMLLoader([-141.49, -500.0, 23.17], () => switchView(3), 3),

      // from view 1
      fromSecond: createHTMLLoader([500.0, 156.76, -238.49], () => switchView(0), 0),
      secondToThird: createHTMLLoader([500.0, -97.39, -310.01], () => switchView(2), 2),

      // from view 2
      fromThird: createHTMLLoader([500.0, 408.64, -32.33], () => switchView(0), 0),
      thirdToFourth: createHTMLLoader([500.0, -35.83, -337.25], () => switchView(3), 3),

      // from view 3
      fromFourth: createHTMLLoader([-198.78, 500.0, -90.59], () => switchView(0), 0),
      fourthToThird: createHTMLLoader([301.62, 59.31, -500.0], () => switchView(2), 2),
    };

    const clearSceneObjects = () => {
      Object.values(loaders).forEach((loader) => {
        if (labelContainer.contains(loader.element)) labelContainer.removeChild(loader.element);
      });

      labelsFirstImage.forEach((label) => {
        if (labelContainer.contains(label.element)) labelContainer.removeChild(label.element);
        if (labelContainer.contains(label.line)) labelContainer.removeChild(label.line);
        if (labelContainer.contains(label.point)) labelContainer.removeChild(label.point);
      });

      labelsEntrance.forEach((label) => {
        if (labelContainer.contains(label.element)) labelContainer.removeChild(label.element);
        if (labelContainer.contains(label.line)) labelContainer.removeChild(label.line);
        if (labelContainer.contains(label.point)) labelContainer.removeChild(label.point);
      });

      popupHotspotsFirstImage.forEach((hotspot) => {
        if (labelContainer.contains(hotspot.hotspotElement)) labelContainer.removeChild(hotspot.hotspotElement);
        if (labelContainer.contains(hotspot.popupElement)) labelContainer.removeChild(hotspot.popupElement);
      });
    };

    const addSceneObjects = (viewIndex) => {
      // view 0
      if (viewIndex === 0) {
        labelContainer.appendChild(loaders.toSecond.element);
        labelContainer.appendChild(loaders.toThird.element);
        labelContainer.appendChild(loaders.toFourth.element);

        labelsFirstImage.forEach((label) => {
          labelContainer.appendChild(label.element);
          labelContainer.appendChild(label.line);
          labelContainer.appendChild(label.point);
        });

        popupHotspotsFirstImage.forEach((hotspot) => {
          labelContainer.appendChild(hotspot.hotspotElement);
          labelContainer.appendChild(hotspot.popupElement);
        });
      }

      // view 1
      if (viewIndex === 1) {
        labelContainer.appendChild(loaders.fromSecond.element);
        labelContainer.appendChild(loaders.secondToThird.element);

        labelsEntrance.forEach((label) => {
          labelContainer.appendChild(label.element);
          labelContainer.appendChild(label.line);
          labelContainer.appendChild(label.point);
        });
      }

      // view 2
      if (viewIndex === 2) {
        labelContainer.appendChild(loaders.fromThird.element);
        labelContainer.appendChild(loaders.thirdToFourth.element);
      }

      // view 3
      if (viewIndex === 3) {
        labelContainer.appendChild(loaders.fromFourth.element);
        labelContainer.appendChild(loaders.fourthToThird.element);
      }
    };

    const faces = ["f", "b", "u", "d", "l", "r"];
    const tileSize = 512;

    const loadTilesForFace = async (face, level, basePath) => {
      const tilesPerSide = Math.pow(2, level - 1);
      const canvasSize = tileSize * tilesPerSide;
      const faceCanvas = document.createElement("canvas");
      faceCanvas.width = canvasSize;
      faceCanvas.height = canvasSize;
      const ctx = faceCanvas.getContext("2d");

      const tilePromises = [];
      for (let y = 0; y < tilesPerSide; y++) {
        for (let x = 0; x < tilesPerSide; x++) {
          const tilePath = `${basePath}/${level}/${face}/${y}/${x}.jpg`;
          tilePromises.push(
            new Promise((resolve) => {
              const image = new Image();
              image.crossOrigin = "Anonymous";
              image.onload = () => {
                ctx.drawImage(image, x * tileSize, y * tileSize, tileSize, tileSize);
                resolve();
              };
              image.onerror = () => {
                ctx.fillStyle = "#000000";
                ctx.fillRect(x * tileSize, y * tileSize, tileSize, tileSize);
                resolve();
              };
              image.src = tilePath;
            })
          );
        }
      }
      await Promise.all(tilePromises);

      let finalCanvas = faceCanvas;
      if (face === "u" || face === "d") {
        const rotatedCanvas = document.createElement("canvas");
        rotatedCanvas.width = canvasSize;
        rotatedCanvas.height = canvasSize;
        const rotatedCtx = rotatedCanvas.getContext("2d");
        rotatedCtx.translate(canvasSize / 2, canvasSize / 2);
        rotatedCtx.rotate(face === "u" ? -Math.PI / 2 : Math.PI / 2);
        rotatedCtx.translate(-canvasSize / 2, -canvasSize / 2);
        rotatedCtx.drawImage(faceCanvas, 0, 0);
        finalCanvas = rotatedCanvas;
      }

      const texture = new THREE.Texture(finalCanvas);
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.minFilter = THREE.LinearFilter;
      texture.magFilter = THREE.LinearFilter;
      texture.anisotropy = Math.min(renderer.capabilities.getMaxAnisotropy(), 8);
      texture.needsUpdate = true;

      return new THREE.MeshBasicMaterial({
        map: texture,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 0,
      });
    };

    const loadViewTiles = async (viewIndex, eventHandlers, cam, initialLevel = 2) => {
      setLoadedLevel(0);

      opacityRef.current.target = 0;
      await new Promise((r) => setTimeout(r, 300));

      clearSceneObjects();

      // dispose previous materials
      if (Array.isArray(cube.material)) {
        cube.material.forEach((mat) => {
          if (mat.map) mat.map.dispose();
          mat.dispose();
        });
      } else if (cube.material) {
        if (cube.material.map) cube.material.map.dispose();
        cube.material.dispose();
      }

      try {
        const materials = await Promise.all(
          faces.map((face) => loadTilesForFace(face, initialLevel, BASE_PATHS[viewIndex]))
        );
        cube.material = materials;
        setLoadedLevel(initialLevel);

        opacityRef.current.value = 0;
        opacityRef.current.target = 1;

        addSceneObjects(viewIndex);

        eventHandlers.state.currentLon = initialAngles[viewIndex].yaw;
        eventHandlers.state.targetLon = initialAngles[viewIndex].yaw;
        eventHandlers.state.currentLat = initialAngles[viewIndex].pitch;
        eventHandlers.state.targetLat = initialAngles[viewIndex].pitch;

        const yaw = THREE.MathUtils.degToRad(initialAngles[viewIndex].yaw);
        const pitch = THREE.MathUtils.degToRad(initialAngles[viewIndex].pitch);
        cam.rotation.order = "YXZ";
        cam.rotation.set(pitch, yaw, 0);

        const highResMaterials = await Promise.all(
          faces.map((face) => loadTilesForFace(face, 3, BASE_PATHS[viewIndex]))
        );

        if (Array.isArray(cube.material)) {
          cube.material.forEach((mat) => {
            if (mat.map) mat.map.dispose();
            mat.dispose();
          });
        }
        cube.material = highResMaterials;
        setLoadedLevel(3);
      } catch (err) {
        setError(`Failed to load tiles: ${err.message}`);
        cube.material = new THREE.MeshBasicMaterial({
          color: 0xff0000,
          side: THREE.DoubleSide,
          transparent: true,
          opacity: 1,
        });
        setLoadedLevel(3);
        return;
      }

      renderer.render(scene, cam);
      cssRenderer.render(scene, cam);
    };

    const handleInteractionStart = () => {};
    const handleInteractionEnd = () => {};

    /** ✅ Correct loader keys (only existing keys) */
    const activeLoaderKeys = [
      ["toSecond", "toThird", "toFourth"], // view 0
      ["fromSecond", "secondToThird"], // view 1
      ["fromThird", "thirdToFourth"], // view 2
      ["fromFourth", "fourthToThird"], // view 3
    ];

    const activeKeys = activeLoaderKeys[currentView] || activeLoaderKeys[0];
    const activeLoaders = {};
    activeKeys.forEach((key) => {
      if (loaders[key]) activeLoaders[key] = loaders[key];
    });

    const eventHandlers = setupEventHandlers({
      scene,
      camera,
      labelsFirstImage,
      clickableObjects: activeLoaders,
      labels: {},
      renderer,
      cssRenderer,
      onViewChange: (viewIndex) => {
        setCurrentView(viewIndex);
        loadViewTiles(viewIndex, eventHandlers, camera);
      },
      onInteractionStart: handleInteractionStart,
      onInteractionEnd: handleInteractionEnd,
    });

    const cleanupZoomHandlers = setupZoomHandlers({
      camera,
      renderer,
      onInteractionStart: handleInteractionStart,
      onInteractionEnd: handleInteractionEnd,
    });

    eventHandlers.state.currentLon = initialAngles[currentView].yaw;
    eventHandlers.state.targetLon = initialAngles[currentView].yaw;
    eventHandlers.state.currentLat = initialAngles[currentView].pitch;
    eventHandlers.state.targetLat = initialAngles[currentView].pitch;

    const initialYaw = THREE.MathUtils.degToRad(initialAngles[currentView].yaw);
    const initialPitch = THREE.MathUtils.degToRad(initialAngles[currentView].pitch);
    camera.rotation.order = "YXZ";
    camera.rotation.set(initialPitch, initialYaw, 0);

    loadViewTiles(currentView, eventHandlers, camera);

    let animationFrameId;
    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);

      opacityRef.current.value = THREE.MathUtils.lerp(opacityRef.current.value, opacityRef.current.target, 0.15);

      if (Array.isArray(cube.material)) {
        cube.material.forEach((mat) => (mat.opacity = opacityRef.current.value));
      } else if (cube.material) {
        cube.material.opacity = opacityRef.current.value;
      }

      const isTransitioning = opacityRef.current.value < 0.95;

      // labels (only view 0 & 1)
      const labels = currentView === 0 ? labelsFirstImage : currentView === 1 ? labelsEntrance : [];
      labels.forEach((label) => {
        const vector = label.position.clone().project(camera);
        const isBehind = vector.z > 1;
        const isInView = vector.x >= -1 && vector.x <= 1 && vector.y >= -1 && vector.y <= 1;

        if (!isBehind && isInView && !isTransitioning) {
          const x = (vector.x * 0.5 + 0.5) * window.innerWidth;
          const y = (-vector.y * 0.5 + 0.5) * window.innerHeight;

          label.element.style.left = `${x}px`;
          label.element.style.top = `${y - 50}px`;
          label.element.style.transform = "translate(-50%, -100%)";
          label.element.style.display = "flex";

          label.point.style.left = `${x}px`;
          label.point.style.top = `${y}px`;
          label.point.style.transform = "translate(-50%, -50%)";
          label.point.style.display = "block";

          const labelBottomY = y - 50;
          const labelBottomX = x;
          const pointX = x;
          const pointY = y;

          const dx = labelBottomX - pointX;
          const dy = labelBottomY - pointY;
          const length = Math.sqrt(dx * dx + dy * dy);
          const angle = Math.atan2(dy, dx) * (180 / Math.PI);

          label.line.style.width = `${length}px`;
          label.line.style.height = "2px";
          label.line.style.left = `${pointX}px`;
          label.line.style.top = `${pointY}px`;
          label.line.style.transform = `rotate(${angle}deg)`;
          label.line.style.transformOrigin = "0 0";
          label.line.style.display = "block";
        } else {
          label.element.style.display = "none";
          label.line.style.display = "none";
          label.point.style.display = "none";
        }
      });

      // popup hotspots only view 0
      const hotspots = currentView === 0 ? popupHotspotsFirstImage : [];
      hotspots.forEach((hotspot) => {
        const v = hotspot.position.clone().project(camera);
        const isBehind = v.z > 1;
        const isInView = v.x >= -1 && v.x <= 1 && v.y >= -1 && v.y <= 1;

        if (!isBehind && isInView && !isTransitioning) {
          const x = (v.x * 0.5 + 0.5) * window.innerWidth;
          const y = (-v.y * 0.5 + 0.5) * window.innerHeight;

          const distance = camera.position.distanceTo(hotspot.position);
          const baseScale = 1000 / distance;
          const fovFactor = Math.tan(THREE.MathUtils.degToRad(camera.fov / 2));
          const adjustedScale = (baseScale / fovFactor) * 0.5;
          const clampedScale = Math.min(Math.max(adjustedScale, 0.9), 1.1);

          hotspot.hotspotElement.style.left = `${x}px`;
          hotspot.hotspotElement.style.top = `${y}px`;
          hotspot.hotspotElement.style.transform = `translate(-50%, -50%) scale(${clampedScale})`;
          hotspot.hotspotElement.style.display = "flex";

          hotspot.popupElement.style.left = `${x}px`;
          hotspot.popupElement.style.top = `${y - 20}px`;
          hotspot.popupElement.style.transform = `translate(-50%, -100%) scale(${clampedScale})`;
        } else {
          hotspot.hotspotElement.style.display = "none";
          hotspot.popupElement.style.display = "none";
        }
      });

      // loaders
      Object.values(loaders).forEach((loader) => {
        const vector = loader.position.clone().project(camera);
        const isBehind = vector.z > 1;
        const isInView = vector.x >= -1 && vector.x <= 1 && vector.y >= -1 && vector.y <= 1;

        if (!isBehind && isInView && !isTransitioning) {
          const x = (vector.x * 0.5 + 0.5) * window.innerWidth;
          const y = (-vector.y * 0.5 + 0.5) * window.innerHeight;

          const distance = camera.position.distanceTo(loader.position);
          const baseScale = 1000 / distance;
          const fovFactor = Math.tan(THREE.MathUtils.degToRad(camera.fov / 2));
          const adjustedScale = (baseScale / fovFactor) * 0.5;
          const clampedScale = Math.min(Math.max(adjustedScale, 0.9), 1.1);

          loader.element.style.left = `${x}px`;
          loader.element.style.top = `${y}px`;
          loader.element.style.transform = `translate(-50%, -50%) scale(${clampedScale})`;
          loader.element.style.display = "flex";
        } else {
          loader.element.style.display = "none";
        }
      });

      // camera smoothing
      eventHandlers.state.currentLat = THREE.MathUtils.lerp(eventHandlers.state.currentLat, eventHandlers.state.targetLat, 0.1);
      eventHandlers.state.currentLon = THREE.MathUtils.lerp(eventHandlers.state.currentLon, eventHandlers.state.targetLon, 0.1);

      const yaw = THREE.MathUtils.degToRad(eventHandlers.state.currentLon);
      const pitch = THREE.MathUtils.degToRad(eventHandlers.state.currentLat);
      camera.rotation.order = "YXZ";
      camera.rotation.set(pitch, yaw, 0);

      setCompassAngle(normalizeAngle(eventHandlers.state.currentLon - yawOffsets[currentView]));

      renderer.render(scene, camera);
      cssRenderer.render(scene, camera);
    };

    animate();
    camera.position.set(0, 0, 0);

    const throttledPointerMove = throttle(eventHandlers.onPointerMove, 50);
    const throttledTouchMove = throttle(eventHandlers.onTouchMove, 50);

    document.addEventListener("mousedown", eventHandlers.onPointerDown, false);
    document.addEventListener("mousemove", throttledPointerMove, false);
    document.addEventListener("mouseup", eventHandlers.onPointerUp, false);

    document.addEventListener("touchstart", eventHandlers.onTouchStart, false);
    document.addEventListener("touchmove", throttledTouchMove, false);
    document.addEventListener("touchend", eventHandlers.onTouchEnd, false);

    document.addEventListener("click", eventHandlers.onDocumentClick, false);
    window.addEventListener("resize", eventHandlers.onWindowResize);

    return () => {
      cancelAnimationFrame(animationFrameId);

      document.removeEventListener("mousedown", eventHandlers.onPointerDown);
      document.removeEventListener("mousemove", throttledPointerMove);
      document.removeEventListener("mouseup", eventHandlers.onPointerUp);

      document.removeEventListener("touchstart", eventHandlers.onTouchStart);
      document.removeEventListener("touchmove", throttledTouchMove);
      document.removeEventListener("touchend", eventHandlers.onTouchEnd);

      document.removeEventListener("click", eventHandlers.onDocumentClick);
      window.removeEventListener("resize", eventHandlers.onWindowResize);

      cleanupZoomHandlers();

      scene.remove(cube);
      cube.geometry.dispose();

      if (Array.isArray(cube.material)) {
        cube.material.forEach((mat) => {
          if (mat.map) mat.map.dispose();
          mat.dispose();
        });
      } else if (cube.material) {
        if (cube.material.map) cube.material.map.dispose();
        cube.material.dispose();
      }

      renderer.dispose();
      renderer.forceContextLoss();

      if (mount.contains(renderer.domElement)) mount.removeChild(renderer.domElement);
      if (mount.contains(cssRenderer.domElement)) mount.removeChild(cssRenderer.domElement);
      if (mount.contains(labelContainer)) mount.removeChild(labelContainer);
    };
  }, [currentView]);

  return (
    <div
      ref={mountRef}
      style={{
        width: "100vw",
        height: "100vh",
        position: "relative",
        overflow: "hidden",
        background: "#000000",
      }}
    >
      {error ? (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(80, 0, 0, 0.9)",
            color: "#ff9999",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2000,
            fontFamily: "Arial, sans-serif",
            fontSize: "18px",
            textAlign: "center",
            padding: "20px",
          }}
        >
          {error}
          <br />
          Please ensure your browser supports WebGL and your graphics drivers are up to date.
        </div>
      ) : (
        <>
          {loadedLevel < 2 && (
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "250px",
                height: "220px",
                background: "rgba(0, 0, 0, 0.7)",
                color: "white",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 1001,
                fontFamily: "Arial, sans-serif",
                fontSize: "20px",
                fontWeight: "bold",
                borderRadius: "30px",
              }}
            >
              Loading...
            </div>
          )}

          <div
            style={{
              position: "fixed",
              top: "140px",
              left: "20px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              color: "#ffffff",
              fontFamily: "Montserrat, sans-serif",
              fontSize: "18px",
              fontWeight: 500,
              zIndex: 3000,
            }}
          >
            <img
              src={Pano}
              alt="Panorama icon"
              style={{ width: "24px", height: "24px", objectFit: "contain" }}
            />
            <span>{VIEW_NAMES[currentView]}</span>
          </div>

          {/* ✅ pass view names so control bar shows only 4 */}
          <ControlBar onViewChange={(idx) => setCurrentView(idx)} views={VIEW_NAMES} />

          <div
            style={{
              position: "absolute",
              top: "20px",
              right: "20px",
              width: "90px",
              height: "90px",
              borderRadius: "50%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 3000,
              opacity: "80%",
              boxShadow: "0 4px 15px rgba(40, 40, 41, 0.5), inset 0 0 10px rgba(0, 0, 0, 0.1)",
              border: "2px solid rgba(255, 255, 255, 1)",
              outline: "4px solid rgba(0, 0, 0, 0.73)",
              outlineOffset: "-1px",
            }}
          >
            <div
              style={{
                position: "absolute",
                width: "90px",
                height: "90px",
                background: `conic-gradient(from ${-compassAngle - 10}deg at 50% 50%, #000000 0deg, #000000 80deg, transparent 80deg, transparent 360deg)`,
                borderRadius: "50%",
                transform: "translate(-50%, -50%)",
                left: "50%",
                top: "50%",
              }}
            />
            <div
              style={{
                width: "10px",
                height: "10px",
                background: "rgb(0, 0, 0)",
                borderRadius: "3px",
                position: "absolute",
                boxShadow: "0 0 8px rgba(255, 255, 255, 0.5)",
              }}
            />
            {["N", "S", "W", "E"].map((d) => (
              <div
                key={d}
                style={{
                  position: "absolute",
                  color: "white",
                  fontSize: "14px",
                  fontFamily: "Arial, sans-serif",
                  ...(d === "N" ? { top: "5px", left: "50%", transform: "translateX(-50%)" } : {}),
                  ...(d === "S" ? { bottom: "5px", left: "50%", transform: "translateX(-50%)" } : {}),
                  ...(d === "W" ? { left: "5px", top: "50%", transform: "translateY(-50%)" } : {}),
                  ...(d === "E" ? { right: "5px", top: "50%", transform: "translateY(-50%)" } : {}),
                }}
              >
                {d}
              </div>
            ))}
          </div>
        </>
      )}

      <div
        style={{
          position: "fixed",
          bottom: "10px",
          right: "9px",
          fontFamily: "'Montserrat', sans-serif",
          fontSize: "9px",
          color: "white",
          borderRadius: "5px",
          textAlign: "end",
          zIndex: 1500,
          pointerEvents: "none",
        }}
      >
        <strong>360° Interactive Virtual tour</strong>
        <br />
        Powered by Luminexa Technologies
      </div>
    </div>
  );
};

export default Tour;
